
import { getDataSourceById } from './mockDataRegistry';
import { MetricData, ChartData } from './apiHooks';

function applyFilters(baseValue, filters?) {
  let adjustedValue = baseValue;
  
  // Apply context filter effect (simulated)
  if (filters?.context && filters.context !== 'all') {
    const contexts = Array.isArray(filters.context) ? filters.context : [filters.context];
    // Simulate different values for different contexts
    if (contexts.length === 1) {
      const contextMultipliers: Record<string, number> = {
        'at-home': 0.95,
        'in-the-classroom': 1.05,
        'on-the-playground': 0.98,
        'online': 0.85,
        'with-my-friends': 1.05,
        'on-the-bus': 0.88,
        'playing-sport': 1.0,
        'social-media-gaming': 0.92,
      };
      adjustedValue *= contextMultipliers[contexts[0]] || 1;
    }
  }
  
  // Apply period filter effect (simulated)
  if (filters?.period) {
    const periodMultipliers: Record<string, number> = {
      week: 1.0,
      month: 0.98,
      term: 0.95,
    };
    adjustedValue *= periodMultipliers[filters.period] || 1;
  }
  
  return Math.round(adjustedValue);
}

export function generateMockMetricData(
  widget,
  comparisonPeriod,
  comparisonDateRange
) {
  const config = widget.config;
  
  // Base values for emotional states (these should sum to ~100% when all selected)
  const emotionStateValues = {
    'ready-to-learn': 35,
    'processing': 25,
    'heightened-emotion': 15,
    'not-ready-to-learn': 15,
    'emotional-overload': 10,
  };
  
  // Get metric type and source ID from config
  const metricType = config?.metricType || 'emotional-state';
  const sourceId = config?.selectedSourceId || widget.dataSources?.[0]?.sourceId || 'ready-to-learn';
  
  let baseValue;
  let trend;
  
  // Set base values based on metric type
  if (metricType === 'emotional-state') {
    // Extract the state ID from sourceId (remove 'emotion-' prefix if present)
    const stateId = sourceId.replace(/^emotion-/, '');
    if (emotionStateValues[stateId]) {
      baseValue = emotionStateValues[stateId];
      trend = stateId === 'ready-to-learn' ? 3 : stateId === 'processing' ? 2 : stateId === 'not-ready-to-learn' ? -2 : stateId === 'emotional-overload' ? -1 : 1;
    } else {
      baseValue = emotionStateValues['ready-to-learn'];
      trend = 3;
    }
  } else if (metricType === 'emotion-response') {
    // Generate random base value for emotion responses (5-25% range)
    const seed = sourceId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    baseValue = 5 + (seed % 20);
    trend = (seed % 3) - 1; // -1, 0, or 1
  } else if (metricType === 'context') {
    // Generate base value for contexts (varies by context)
    const contextMultipliers: Record<string, number> = {
      'at-home': 0.9,
      'in-the-classroom': 1.1,
      'on-the-playground': 0.95,
      'online': 0.85,
      'with-my-friends': 1.05,
      'on-the-bus': 0.88,
      'playing-sport': 1.0,
      'social-media-gaming': 0.92,
    };
    const contextId = sourceId.replace(/^context-/, '');
    const multiplier = contextMultipliers[contextId] || 1.0;
    baseValue = 40 * multiplier; // Base around 40%, adjusted by context
    trend = (contextId.charCodeAt(0) % 3) - 1;
  } else if (metricType === 'check-in-rate') {
    // Check-in rate calculation
    const threshold = config?.checkInThreshold;
    const period = threshold?.period || 'week';
    const daysPerWeek = threshold?.daysPerWeek || (period === 'day' ? 1 : period === 'month' ? 10 : period === 'term' ? 30 : 3);
    
    // Base check-in rate (students who meet threshold)
    // Higher days per week requirement = lower percentage of students who qualify
    let baseValue;
    if (period === 'day') {
      baseValue = 75 - (daysPerWeek - 1) * 10; // 1 check-in = ~75%, but should be high for day
    } else {
      baseValue = 85 - (daysPerWeek - 1) * 8; // 3 days = ~69%, 7 days = ~53%
    }
    
    // Apply comparison adjustment if this is comparison data
    if (comparisonPeriod) {
      const comparisonMultipliers: Record<string, number> = {
        'previous-week': 0.95, // 5% lower
        'previous-month': 0.92, // 8% lower
        'previous-term': 0.88, // 12% lower
        'custom': 0.94, // Default 6% lower
      };
      baseValue *= comparisonMultipliers[comparisonPeriod] || 0.95;
      trend = 0; // Comparison data has no trend
    } else {
      trend = 3;
    }
    
    // Apply date range multipliers
    const dateRange = comparisonDateRange || config?.dateRange;
    if (dateRange?.period && !comparisonDateRange) {
      const periodMultipliers: Record<string, number> = {
        today: 1.0,
        week: 1.0,
        month: 0.98,
        term: 0.95,
      };
      baseValue *= periodMultipliers[dateRange.period] || 1.0;
    }
    
    // Calculate count and total for "#/total" format
    const totalStudents = 200; // Mock total number of students
    const count = Math.round((baseValue / 100) * totalStudents);
    
    // Return format with count/total and percentage
    const percentage = ((count / totalStudents) * 100).toFixed(1);
    return {
      value: `${count}/${totalStudents} (${percentage}%)`,
      count,
      total: totalStudents,
      trend: comparisonPeriod ? '0%' : `${trend > 0 ? '+' : ''}${trend}%`,
      trendDirection: trend > 0 ? 'up' : trend < 0 ? 'down' : 'neutral',
    };
  } else {
    // Default fallback
    baseValue = emotionStateValues['ready-to-learn'];
    trend = 3;
  }
  
  // If this is comparison data, adjust the base value to simulate previous period
  if (comparisonPeriod) {
    // Apply a slight variation to simulate previous period (typically slightly lower)
    const comparisonMultipliers: Record<string, number> = {
      'previous-week': 0.95, // 5% lower
      'previous-month': 0.92, // 8% lower
      'previous-term': 0.88, // 12% lower
      'custom': 0.94, // Default 6% lower
    };
    baseValue *= comparisonMultipliers[comparisonPeriod] || 0.95;
    // Comparison data has no trend (it's historical)
    trend = 0;
  }
  
  // Apply date range from config (affects the value)
  // Use comparison date range if provided, otherwise use config date range
  const dateRange = comparisonDateRange || config?.dateRange;
  if (dateRange?.period && !comparisonDateRange) {
    const periodMultipliers: Record<string, number> = {
      today: 1.0,
      week: 1.0,
      month: 0.98,
      term: 0.95,
    };
    baseValue *= periodMultipliers[dateRange.period] || 1.0;
  } else if (comparisonPeriod && !comparisonDateRange) {
    // For comparison periods, use the period multipliers
    const periodMultipliers: Record<string, number> = {
      'previous-week': 1.0,
      'previous-month': 0.98,
      'previous-term': 0.95,
    };
    baseValue *= periodMultipliers[comparisonPeriod] || 1.0;
  }
  
  // Apply filters from first data source if available
  const filters = widget.dataSources?.[0]?.filters;
  const adjustedValue = applyFilters(baseValue, filters);
  
  // Apply check-in threshold if it's check-in-rate metric
  let finalValue = adjustedValue;
  if (metricType === 'check-in-rate' && config?.checkInThreshold) {
    // Higher days per week requirement means fewer students qualify
    const daysPerWeek = config.checkInThreshold.daysPerWeek || 3;
    // Apply additional penalty based on period if specified
    const periodMultiplier = config.checkInThreshold.period === 'month' ? 0.95 : 
                            config.checkInThreshold.period === 'term' ? 0.90 : 1.0;
    finalValue = Math.max(0, Math.min(100, adjustedValue * periodMultiplier));
  }

  const trendDirection: 'up' | 'down' | 'neutral' = trend > 0 ? 'up' : trend < 0 ? 'down' : 'neutral';
  
  const format = config?.format || 'percentage';
  const decimalPlaces = config?.decimalPlaces ?? 1;
  
  let formattedValue;
  if (format === 'percentage') {
    formattedValue = `${finalValue.toFixed(decimalPlaces)}%`;
  } else {
    formattedValue = finalValue.toFixed(decimalPlaces);
  }

  return {
    value: formattedValue,
    trend: trend > 0 ? `+${trend}%` : `${trend}%`,
    trendDirection,
  };
}

/**
 * Generate mock survey data based on the bar-survey.json structure
 * Returns the first question's data as a Chart.js-compatible format
 */
function generateMockSurveyData() {
  // This is the first question from bar-survey.json: "How do you wake up?"
  return {
    labels: ["Early Stage 1", "Stage 1", "Stage 2", "Stage 3"],
    datasets: [
      {
        label: "Someone wakes me (Check-in Category - Students who are rarely tired: Checked-in as tired < 5% Students who are rarely tired: Checked-in as tired < 5% )",
        data: [6, 2, 9, 10],
        backgroundColor: "rgb(210, 235, 255)",
        borderColor: "black",
        tension: 0,
        stack: "stack-rarely",
      },
      {
        label: "By myself (Check-in Category - Students who are rarely tired: Checked-in as tired < 5% Students who are rarely tired: Checked-in as tired < 5% )",
        data: [4, 9, 6, 6],
        backgroundColor: "rgb(173, 216, 255)",
        borderColor: "black",
        tension: 0,
        stack: "stack-rarely",
      },
      {
        label: "I set an alarm (Check-in Category - Students who are rarely tired: Checked-in as tired < 5% Students who are rarely tired: Checked-in as tired < 5% )",
        data: [4, 7, 4, 4],
        backgroundColor: "rgb(113, 191, 245)",
        borderColor: "black",
        tension: 0,
        stack: "stack-rarely",
      },
      {
        label: "Someone wakes me (Check-in Category - Students who are sometimes tired: Checked-in as tired 5-15% Students who are sometimes tired: Checked-in as tired 5-15% )",
        data: [3, 6, 9, 2],
        backgroundColor: "rgb(255, 235, 210)",
        borderColor: "black",
        tension: 0,
        stack: "stack-sometimes",
      },
      {
        label: "By myself (Check-in Category - Students who are sometimes tired: Checked-in as tired 5-15% Students who are sometimes tired: Checked-in as tired 5-15% )",
        data: [8, 5, 1, 2],
        backgroundColor: "rgb(255, 219, 180)",
        borderColor: "black",
        tension: 0,
        stack: "stack-sometimes",
      },
      {
        label: "I set an alarm (Check-in Category - Students who are sometimes tired: Checked-in as tired 5-15% Students who are sometimes tired: Checked-in as tired 5-15% )",
        data: [3, 10, 3, 10],
        backgroundColor: "rgb(255, 191, 128)",
        borderColor: "black",
        tension: 0,
        stack: "stack-sometimes",
      },
      {
        label: "Someone wakes me (Check-in Category - Students who are regularly tired: Checked-in as tired > 15% Students who are regularly tired: Checked-in as tired > 15% )",
        data: [1, 2, 10, 8],
        backgroundColor: "rgb(214, 250, 241)",
        borderColor: "black",
        tension: 0,
        stack: "stack-regularly",
      },
      {
        label: "By myself (Check-in Category - Students who are regularly tired: Checked-in as tired > 15% Students who are regularly tired: Checked-in as tired > 15% )",
        data: [2, 5, 4, 4],
        backgroundColor: "rgb(184, 242, 230)",
        borderColor: "black",
        tension: 0,
        stack: "stack-regularly",
      },
      {
        label: "I set an alarm (Check-in Category - Students who are regularly tired: Checked-in as tired > 15% Students who are regularly tired: Checked-in as tired > 15% )",
        data: [1, 2, 10, 4],
        backgroundColor: "rgb(129, 217, 202)",
        borderColor: "black",
        tension: 0,
        stack: "stack-regularly",
      },
      {
        label: "Someone wakes me ( All Students [total]: Checked-in as tired 0-100% )",
        data: [8, 7, 7, 4],
        backgroundColor: "rgb(240, 225, 255)",
        borderColor: "black",
        tension: 0,
        stack: "stack-total",
      },
      {
        label: "By myself ( All Students [total]: Checked-in as tired 0-100% )",
        data: [9, 6, 7, 2],
        backgroundColor: "rgb(220, 198, 255)",
        borderColor: "black",
        tension: 0,
        stack: "stack-total",
      },
      {
        label: "I set an alarm ( All Students [total]: Checked-in as tired 0-100% )",
        data: [8, 10, 1, 9],
        backgroundColor: "rgb(186, 151, 255)",
        borderColor: "black",
        tension: 0,
        stack: "stack-total",
      },
    ],
  };
}

export function generateMockChartData(widget) {
  const config = widget.config;
  const surveyGrouping = config?.surveyGrouping;
  
  // If survey grouping is enabled, return the survey mock data
  if (surveyGrouping?.enabled && widget.type === 'bar-chart') {
    return generateMockSurveyData();
  }

  // Generate time series data for charts
  const enabledSources = widget.dataSources.filter((ds) => ds.enabled);
  
  if (enabledSources.length === 0) {
    return {
      labels: [],
      datasets: [],
    };
  }

  const timeAxis = config?.timeAxis || 'week';
  const comparisonConfig = config?.comparison;
  const chartType = config?.chartType || (widget.type === 'bar-chart' ? 'bar' : 'line');
  
  // For pie/doughnut charts, generate different data structure
  if (chartType === 'pie' || chartType === 'doughnut') {
    return generateMockPieChartData(widget, chartType);
  }
  
  // For bar charts, check if we're using metric sources (engagement, context) vs time-series
  const hasMetricSources = enabledSources.some(source => {
    const sourceInfo = getDataSourceById(source.sourceId);
    return sourceInfo && sourceInfo.dataType === 'metric';
  });
  
  // Generate labels based on chart type and data source type
  let labels = [];
  
  // Special handling for horizontal bar charts with dummyData (single stacked bar)
  const isHorizontalWithDummyData = chartType === 'bar' && config?.horizontal === true && config?.dummyData?.enabled === true;
  
  if (isHorizontalWithDummyData) {
    // For horizontal single stacked bar, use a single empty label
    labels = [''];
  } else if (chartType === 'bar' && hasMetricSources) {
    // For bar charts with metric sources, generate labels based on what we're grouping
    const groupBy = config?.groupBy || 'none';
    
    if (groupBy === 'class') {
      // Mock class names for class usage
      let allClasses = ['4A', '4B', '5A', '5B', '5C', '6A', '6B'];
      // Apply chart-level class filter if present
      if (config?.filters?.classIds && config.filters.classIds.length > 0) {
        const classIdToName: Record<number, string> = {
          1: '4A', 2: '4B', 3: '5A', 4: '5B', 5: '5C', 6: '6A', 7: '6B', 8: '7A', 9: '7B', 10: '8A'
        };
        labels = config.filters.classIds.map((id) => classIdToName[id]).filter(Boolean);
      } else {
        labels = allClasses;
      }
    } else if (groupBy === 'context') {
      // Context labels - apply chart-level context filter if present
      let allContexts = ['At home', 'In the classroom', 'On the playground', 'Online', 'With my friends', 'On the bus', 'Playing sport', 'Social media / gaming'];
      if (config?.filters?.contextIds && config.filters.contextIds.length > 0) {
        const contextIdToName: Record<string, string> = {
          'context-at-home': 'At home',
          'context-in-the-classroom': 'In the classroom',
          'context-on-the-playground': 'On the playground',
          'context-online': 'Online',
          'context-with-my-friends': 'With my friends',
          'context-on-the-bus': 'On the bus',
          'context-playing-sport': 'Playing sport',
          'context-social-media-gaming': 'Social media / gaming',
        };
        labels = config.filters.contextIds.map((id) => contextIdToName[id]).filter(Boolean);
      } else {
        labels = allContexts;
      }
    } else {
      // Ungrouped: labels are based on data sources themselves
      labels = enabledSources.map(source => source.label);
    }
  } else {
    // Time series labels (for line charts or time-series bar charts)
    if (timeAxis === 'day') {
      labels = Array.from({ length: 14 }, (_, i) => `Day ${i + 1}`);
    } else if (timeAxis === 'week') {
      labels = Array.from({ length: 8 }, (_, i) => `Week ${i + 1}`);
    } else if (timeAxis === 'month') {
      labels = Array.from({ length: 6 }, (_, i) => `Month ${i + 1}`);
    } else {
      labels = ['Term 1', 'Term 2', 'Term 3', 'Term 4'];
    }
  }

  // Base values for emotional states (percentages)
  // These should sum to approximately 100% for stacked charts
  const emotionStateBaseValues: Record<string, number> = {
    'ready-to-learn': 30,
    'processing': 20,
    'not-ready': 20,
    'heightened-emotion': 15,
    'emotional-overload': 15,
  };

  // Helper function to convert hex/rgb/hsl color to rgba with opacity
  const addOpacityToColor = (color, opacity) => {
    // If color is already rgba/rgb, extract RGB values
    if (color.startsWith('rgba') || color.startsWith('rgb')) {
      const match = color.match(/\d+/g);
      if (match && match.length >= 3) {
        return `rgba(${match[0]}, ${match[1]}, ${match[2]}, ${opacity})`;
      }
    }
    // If color is hex (#RRGGBB)
    if (color.startsWith('#')) {
      const hex = color.slice(1);
      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);
      return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    }
    // If color is hsl, convert to hsla
    if (color.startsWith('hsl')) {
      // Extract HSL values and convert to HSLA
      const hslMatch = color.match(/hsl\((\d+),\s*(\d+)%,\s*(\d+)%\)/);
      if (hslMatch) {
        return `hsla(${hslMatch[1]}, ${hslMatch[2]}%, ${hslMatch[3]}%, ${opacity})`;
      }
      // If already hsla, just update opacity
      const hslaMatch = color.match(/hsla\((\d+),\s*(\d+)%,\s*(\d+)%,\s*[\d.]+\)/);
      if (hslaMatch) {
        return `hsla(${hslaMatch[1]}, ${hslaMatch[2]}%, ${hslaMatch[3]}%, ${opacity})`;
      }
    }
    // Fallback: return color as-is
    return color;
  };

  // Extract base color from color string (remove opacity if present)
  const extractBaseColor = (color) => {
    // If rgba/hsla, convert back to rgb/hsl
    if (color.startsWith('rgba')) {
      const match = color.match(/rgba\((\d+),\s*(\d+),\s*(\d+)/);
      if (match) {
        return `rgb(${match[1]}, ${match[2]}, ${match[3]})`;
      }
    }
    if (color.startsWith('hsla')) {
      const match = color.match(/hsla\((\d+),\s*(\d+)%,\s*(\d+)%/);
      if (match) {
        return `hsl(${match[1]}, ${match[2]}%, ${match[3]}%)`;
      }
    }
    return color;
  };

  // Extract opacity from color string if present
  const extractOpacity = (color) => {
    if (color.startsWith('rgba')) {
      const match = color.match(/rgba\([^)]+,\s*([\d.]+)\)/);
      if (match) {
        return parseFloat(match[1]);
      }
    }
    if (color.startsWith('hsla')) {
      const match = color.match(/hsla\([^)]+,\s*([\d.]+)\)/);
      if (match) {
        return parseFloat(match[1]);
      }
    }
    return null;
  };

  // Special handling for horizontal bar charts with dummyData (single stacked bar)
  if (isHorizontalWithDummyData && config.dummyData?.data) {
    const dummyDataArray = config.dummyData.data;
    
    // Create a map of stateOfEmotion to data source for easier lookup
    const sourceMap = new Map();
    enabledSources.forEach((source) => {
      const key = source.label?.toLowerCase().trim();
      if (key) {
        sourceMap.set(key, source);
      }
    });
    
    const datasets = dummyDataArray.map((item) => {
      // Match by stateOfEmotion (exact match first, then fuzzy)
      const itemState = (item.stateOfEmotion || item.state || '').toLowerCase().trim();
      let matchingSource = sourceMap.get(itemState);
      
      // If no exact match, try fuzzy matching
      if (!matchingSource) {
        matchingSource = enabledSources.find((source) => {
          const sourceLabel = source.label?.toLowerCase().trim();
          return (
            sourceLabel === itemState ||
            sourceLabel?.includes(itemState) ||
            itemState?.includes(sourceLabel)
          );
        });
      }
      
      // Get color from matching source or use default
      const sourceColor = matchingSource?.color || '#14B8A6';
      const baseColor = extractBaseColor(sourceColor);
      
      // Get percentage value (for visual width - should be 25% for equal segments)
      const percentage = item.percentage || item.count || 0;
      // Get display percentage (actual percentage to show as label)
      const displayPercentage = item.displayPercentage || item.percentage || item.count || 0;
      
      return {
        label: item.stateOfEmotion || item.state || matchingSource?.label || 'Unknown',
        data: [percentage], // Single value for single bar (25% for equal width)
        backgroundColor: addOpacityToColor(baseColor, 0.85),
        borderColor: addOpacityToColor(baseColor, 1.0),
        stack: 'stack', // All datasets stack together
        displayPercentage: displayPercentage, // Store actual percentage for label display
      };
    });
    
    return {
      labels,
      datasets,
    };
  }

  // Generate base values for each source, then apply filters
  const datasets = enabledSources.flatMap((source) => {
    const sourceInfo = getDataSourceById(source.sourceId);
    const isMetricSource = sourceInfo?.dataType === 'metric';
    
    // For bar charts with metric sources (engagement, context), generate different data structure
    if (chartType === 'bar' && isMetricSource) {
      const groupBy = config?.groupBy || 'none';
      
      // Generate data for each label (class, context, or data source)
      const generateBarData = (isComparison = false) => {
        return labels.map((_label, i) => {
          let value;
          
          // Determine base value based on source type and groupBy
          if (groupBy === 'none') {
            // Ungrouped: single value per data source
            if (sourceInfo?.metricCategory === 'check-in-rate') {
              value = 75 + Math.random() * 15; // 75-90%
            } else if (sourceInfo?.metricCategory === 'context') {
              value = 20 + Math.random() * 20; // 20-40%
            } else if (sourceInfo?.metricCategory === 'emotional-state') {
              // Emotional states: use base values
              const emotionId = source.sourceId;
              value = emotionStateBaseValues[emotionId] || 25;
            } else {
              value = 50 + Math.random() * 30;
            }
          } else if (groupBy === 'class') {
            // Grouped by class: engagement/check-in rate varies by class
            if (sourceInfo?.metricCategory === 'check-in-rate') {
              const baseEngagement = 70 + Math.random() * 20; // 70-90%
              value = baseEngagement + (Math.sin(i * 0.3) * 5); // Some variation per class
            } else {
              // Other metrics (context, emotion-response): vary by class
              value = 50 + Math.random() * 30 + (Math.sin(i * 0.3) * 5);
            }
          } else if (groupBy === 'context') {
            // Grouped by context: emotional states vary by context
            if (sourceInfo?.metricCategory === 'emotional-state') {
              const emotionId = source.sourceId;
              // For count format, generate actual check-in counts instead of percentages
              // Base counts vary by emotional state (some states are more common)
              const baseCounts: Record<string, number> = {
                'ready-to-learn': 150,
                'processing': 100,
                'not-ready': 80,
                'heightened-emotion': 60,
                'emotional-overload': 40,
              };
              const baseCount = baseCounts[emotionId] || 80;
              // Add variation based on context index and emotion type
              // Each context should have different distributions
              const contextVariation = Math.sin((i * 0.7) + (emotionId.charCodeAt(0) % 10)) * 30;
              const randomVariation = (Math.random() * 20 - 10); // -10 to +10
              value = baseCount + contextVariation + randomVariation;
              // Ensure value stays within reasonable bounds (10-300 check-ins per state)
              value = Math.max(10, Math.min(300, value));
              // Round to whole numbers for counts
              value = Math.round(value);
            } else {
              value = 50 + Math.random() * 30;
            }
          } else {
            value = 50 + Math.random() * 30;
          }
          
          // Apply comparison multiplier if needed
          if (isComparison) {
            const comparisonMultipliers: Record<string, number> = {
              'previous-week': 0.95,
              'previous-month': 0.92,
              'previous-term': 0.88,
              'previous-year': 0.85,
              'custom': 0.94,
            };
            const multiplier = comparisonMultipliers[comparisonConfig?.period || 'previous-term'] || 0.94;
            value *= multiplier;
          }
          
          // Apply filters
          if (source.filters) {
            if (source.filters.period) {
              const periodMultipliers: Record<string, number> = {
                week: 1.0,
                month: 0.98,
                term: 0.95,
              };
              value *= periodMultipliers[source.filters.period] || 1;
            }
          }
          
          // For count format, return whole numbers; for percentage, return decimals capped at 100
          const format = config?.format || 'percentage';
          if (format === 'count') {
            return Math.max(0, Math.round(value));
          } else {
            return Math.max(0, Math.min(100, Math.round(value * 10) / 10));
          }
        });
      };
      
      const sourceColor = source.color || sourceInfo?.defaultColor || '#14B8A6'; // Teal (default for Ready to Learn)
      const baseColor = extractBaseColor(sourceColor);
      const userOpacity = extractOpacity(sourceColor);
      
      // Determine opacity for bar charts
      let backgroundColorOpacity;
      let borderColorOpacity;
      
      if (userOpacity !== null) {
        backgroundColorOpacity = userOpacity;
        borderColorOpacity = userOpacity;
      } else {
        backgroundColorOpacity = 0.85;
        borderColorOpacity = 1.0;
      }
      
      const datasetsArray = [];
      
      // Main dataset
      datasetsArray.push({
        label: source.label,
        data: generateBarData(false),
        borderColor: addOpacityToColor(baseColor, borderColorOpacity),
        backgroundColor: addOpacityToColor(baseColor, backgroundColorOpacity),
        // For stacked bars, set stack ID (will be overridden by ChartWidget if needed)
        ...(config?.barDisplayMode === 'stacked' && { stack: 'stack' }),
      });
      
      // Comparison dataset (if enabled)
      if (comparisonConfig?.enabled) {
        let comparisonBorderOpacity;
        let comparisonBgOpacity;
        
        if (userOpacity !== null) {
          comparisonBorderOpacity = userOpacity * 0.5;
          comparisonBgOpacity = userOpacity * 0.5;
        } else {
          comparisonBorderOpacity = 0.5;
          comparisonBgOpacity = 0.4;
        }
        
        datasetsArray.push({
          label: `${source.label} (Comparison)`,
          data: generateBarData(true),
          borderColor: addOpacityToColor(baseColor, comparisonBorderOpacity),
          backgroundColor: addOpacityToColor(baseColor, comparisonBgOpacity),
        });
      }
      
      return datasetsArray;
    }
    
    // Original time-series logic for line charts and time-series bar charts
    // Extract base emotional state ID from weekly source IDs
    let baseSourceId = source.sourceId;
    if (source.sourceId.startsWith('weekly')) {
      // Map weekly IDs to base emotional state IDs
      const weeklyIdMap: Record<string, string> = {
        'weeklyRTL': 'ready-to-learn',
        'weeklyPROC': 'processing',
        'weeklyHE': 'heightened-emotion',
        'weeklyNRTL': 'not-ready-to-learn',
        'weeklyEO': 'emotional-overload',
      };
      baseSourceId = weeklyIdMap[source.sourceId] || source.sourceId;
    }

    // For emotional state sources, use their base percentages
    let baseValue;
    if (emotionStateBaseValues[baseSourceId]) {
      baseValue = emotionStateBaseValues[baseSourceId];
    } else {
      // Fallback for other sources
      const baseSeed = baseSourceId.charCodeAt(0) % 50;
      baseValue = 50 + baseSeed;
    }
    
    // Generate trend data with some variation and spikes
    const generateData = (isComparison = false) => {
      return labels.map((_, i) => {
        // Add some variation over time (sine wave for trends)
        let value = baseValue + (Math.sin(i * 0.5) * 5) + (Math.random() * 3 - 1.5);
        
        // Add spikes for specific emotional states at specific weeks
        // Only apply spikes to main data, not comparison data
        if (!isComparison && timeAxis === 'week' && labels.length >= 8) {
          // Week 3 (index 2): Spike in "ready-to-learn"
          if (baseSourceId === 'ready-to-learn' && i === 2) {
            value = baseValue + 25; // Spike to ~60% (35% base + 25%)
          }
          
          // Week 5 (index 4): Spikes in "not-ready-to-learn" and "emotional-overload"
          if (i === 4) {
            if (baseSourceId === 'not-ready-to-learn') {
              value = baseValue + 20; // Spike to ~35% (15% base + 20%)
            } else if (baseSourceId === 'emotional-overload') {
              value = baseValue + 15; // Spike to ~25% (10% base + 15%)
            }
            // For other states during this week, slightly reduce to show the inverse relationship
            else if (baseSourceId === 'ready-to-learn') {
              value = baseValue - 10; // Dip to ~25% (35% base - 10%)
            }
          }
        }
        
        // For comparison data, apply a multiplier to simulate previous period
        if (isComparison) {
          const comparisonMultipliers: Record<string, number> = {
            'previous-week': 0.95,
            'previous-month': 0.92,
            'previous-term': 0.88,
            'previous-year': 0.85,
            'custom': 0.94,
          };
          const multiplier = comparisonMultipliers[comparisonConfig?.period || 'previous-term'] || 0.94;
          value *= multiplier;
        }
        
        // Apply filters (excluding context for emotional-state sources)
        if (source.filters) {
          // Apply period filter
          if (source.filters.period) {
            const periodMultipliers: Record<string, number> = {
              week: 1.0,
              month: 0.98,
              term: 0.95,
            };
            value *= periodMultipliers[source.filters.period] || 1;
          }
          
          // Don't apply context filter for emotional-state sources (line charts)
          // Context filter is removed for emotional-state sources
        }
        
        return Math.max(0, Math.min(100, Math.round(value * 10) / 10));
      });
    };

    const sourceColor = source.color || '#4CAF50';
    const baseColor = extractBaseColor(sourceColor);
    const userOpacity = extractOpacity(sourceColor);
    
    // Determine opacity based on chart type and user settings
    // For line charts: borderColor (the line) uses user opacity, backgroundColor (fill) uses lower opacity
    // For bar charts: backgroundColor (the bars) uses user opacity, borderColor uses same or solid
    let backgroundColorOpacity;
    let borderColorOpacity;
    
    if (userOpacity !== null) {
      // User has set opacity - use it
      if (chartType === 'bar') {
        backgroundColorOpacity = userOpacity; // Bars use user opacity
        borderColorOpacity = userOpacity; // Bar borders match bar opacity
      } else {
        borderColorOpacity = userOpacity; // Line uses user opacity
        backgroundColorOpacity = userOpacity * 0.2; // Fill area is more transparent
      }
    } else {
      // No user opacity - use defaults
      if (chartType === 'bar') {
        backgroundColorOpacity = 0.85;
        borderColorOpacity = 1.0;
      } else {
        backgroundColorOpacity = 0.2;
        borderColorOpacity = 1.0;
      }
    }
    
    const datasetsArray = [];

    // Main dataset
    datasetsArray.push({
      label: source.label,
      data: generateData(false),
      borderColor: addOpacityToColor(baseColor, borderColorOpacity),
      backgroundColor: addOpacityToColor(baseColor, backgroundColorOpacity),
      tension: 0.4,
    });

    // Comparison dataset (if enabled)
    if (comparisonConfig?.enabled) {
      // For comparison, use half of the user's opacity, or default to 0.5 for border, 0.4/0.1 for background
      let comparisonBorderOpacity;
      let comparisonBgOpacity;
      
      if (userOpacity !== null) {
        comparisonBorderOpacity = userOpacity * 0.5;
        comparisonBgOpacity = userOpacity * 0.5;
      } else {
        comparisonBorderOpacity = 0.5;
        comparisonBgOpacity = chartType === 'bar' ? 0.4 : 0.1;
      }
      
      datasetsArray.push({
        label: `${source.label} (Comparison)`,
        data: generateData(true),
        borderColor: addOpacityToColor(baseColor, comparisonBorderOpacity),
        backgroundColor: addOpacityToColor(baseColor, comparisonBgOpacity),
        tension: 0.4,
        borderDash: [5, 5], // Dashed line for comparison
      });
    }

    return datasetsArray;
  });

  // For stacked bar charts with emotional states grouped by context, normalize so each bar sums to 100%
  const isPercentage = config?.format === 'percentage';
  if (chartType === 'bar' && config?.barDisplayMode === 'stacked' && config?.groupBy === 'context' && isPercentage) {
    const emotionalStateSources = enabledSources.filter(s => {
      const sourceInfo = getDataSourceById(s.sourceId);
      return sourceInfo?.metricCategory === 'emotional-state';
    });
    
    if (emotionalStateSources.length > 0 && labels.length > 0) {
      // Create maps for easier matching
      const sourceLabelToId = new Map();
      const sourceIdToInfo = new Map();
      enabledSources.forEach(s => {
        sourceLabelToId.set(s.label, s.sourceId);
        const sourceInfo = getDataSourceById(s.sourceId);
        if (sourceInfo?.metricCategory === 'emotional-state') {
          sourceIdToInfo.set(s.sourceId, sourceInfo);
        }
      });
      
      // Normalize each label (context) so the sum of all emotional states equals 100%
      labels.forEach((_label, labelIndex) => {
        // Calculate sum of all emotional states for this context (only main datasets, not comparison)
        let sum = 0;
        const emotionalStateDatasets = [];
        
        datasets.forEach((dataset) => {
          // Skip comparison datasets
          if (dataset.label?.includes('(Comparison)')) return;
          
          // Try to match dataset to an emotional state source
          let isEmotionalState = false;
          
          // Method 1: Match by label
          const sourceId = sourceLabelToId.get(dataset.label);
          if (sourceId && sourceIdToInfo.has(sourceId)) {
            isEmotionalState = true;
          } else {
            // Method 2: Match by finding a source with matching label
            const matchingSource = emotionalStateSources.find(s => s.label === dataset.label);
            if (matchingSource) {
              isEmotionalState = true;
            }
          }
          
          if (isEmotionalState && dataset.data[labelIndex] !== undefined) {
            const value = dataset.data[labelIndex];
            if (!isNaN(value) && value > 0) {
              sum += value;
              emotionalStateDatasets.push({ dataset });
            }
          }
        });
        
        // If sum is not 100%, normalize proportionally
        if (sum > 0 && Math.abs(sum - 100) > 0.1) {
          const scaleFactor = 100 / sum;
          emotionalStateDatasets.forEach(({ dataset }) => {
            if (dataset.data[labelIndex] !== undefined) {
              const originalValue = dataset.data[labelIndex];
              const normalizedValue = Math.round(originalValue * scaleFactor * 10) / 10;
              dataset.data[labelIndex] = normalizedValue;
            }
          });
        }
      });
    }
  }

  // For line charts with multiple emotional state datasets, normalize data to ensure no week exceeds 100%
  // This only applies to line charts (not bar charts) and when we have multiple datasets
  if (chartType === 'line' && datasets.length > 1 && labels.length > 0) {
    // Check if we're dealing with emotional states (all datasets should have percentage data)
    const isEmotionalStates = datasets.every((ds) => {
      const sourceInfo = getDataSourceById(
        enabledSources.find((s) => s.label === ds.label || ds.label?.startsWith(s.label))?.sourceId || ''
      );
      return sourceInfo?.metricCategory === 'emotional-state' || 
             enabledSources.some((s) => s.sourceId.startsWith('weekly'));
    });

    if (isEmotionalStates) {
      // Separate main datasets from comparison datasets
      const mainDatasets = datasets.filter((ds) => !ds.label?.includes('(Comparison)'));
      const comparisonDatasets = datasets.filter((ds) => ds.label?.includes('(Comparison)'));

      // Normalize main datasets
      const normalizedMainDatasets = mainDatasets.map((ds) => ({
        ...ds,
        data: [],
      }));

      // Process each time point (week/day/month) for main datasets
      for (let i = 0; i < labels.length; i++) {
        // Get all values for this time point from main datasets only
        const values = mainDatasets.map((ds) => {
          const val = ds.data[i];
          return typeof val === 'number' ? val : 0;
        });

        // Calculate the sum
        const sum = values.reduce((a, b) => a + b, 0);

        // If sum exceeds 100%, normalize proportionally
        if (sum > 100) {
          const scaleFactor = 100 / sum;
          values.forEach((val, idx) => {
            normalizedMainDatasets[idx].data.push(Math.max(0, Math.min(100, Math.round(val * scaleFactor * 10) / 10)));
          });
        } else {
          // If sum is less than or equal to 100%, keep original values
          values.forEach((val, idx) => {
            normalizedMainDatasets[idx].data.push(val);
          });
        }
      }

      // Normalize comparison datasets separately (if they exist)
      let normalizedComparisonDatasets = comparisonDatasets;
      if (comparisonDatasets.length > 0) {
        normalizedComparisonDatasets = comparisonDatasets.map((ds) => ({
          ...ds,
          data: [],
        }));

        // Process each time point for comparison datasets
        for (let i = 0; i < labels.length; i++) {
          const values = comparisonDatasets.map((ds) => {
            const val = ds.data[i];
            return typeof val === 'number' ? val : 0;
          });

          const sum = values.reduce((a, b) => a + b, 0);

          if (sum > 100) {
            const scaleFactor = 100 / sum;
            values.forEach((val, idx) => {
              normalizedComparisonDatasets[idx].data.push(Math.max(0, Math.min(100, Math.round(val * scaleFactor * 10) / 10)));
            });
          } else {
            values.forEach((val, idx) => {
              normalizedComparisonDatasets[idx].data.push(val);
            });
          }
        }
      }

      return {
        labels,
        datasets: [...normalizedMainDatasets, ...normalizedComparisonDatasets],
      };
    }
  }

  return {
    labels,
    datasets,
  };
}

export function generateMockTableData(widget) {
  const config = widget.config;
  const tableConfig = config;
  
  // Support both legacy columns and new columnConfigs
  const columnConfigs = tableConfig?.columnConfigs || [];
  const legacyColumns = tableConfig?.columns || [];
  
  // Determine which column system to use
  const useNewSystem = columnConfigs.length > 0 || tableConfig?.dataSource === 'checkin-summary';
  
  if (useNewSystem && tableConfig?.dataSource === 'checkin-summary') {
    return generateCheckinSummaryTableData(widget, tableConfig);
  }
  
  // Legacy table data generation
  const columns = legacyColumns.length > 0 ? legacyColumns : ['Name', 'Score', 'Status'];
  const rowCount = config?.rowCount || 10;
  const filters = widget.dataSources?.[0]?.filters;

  const rows = Array.from({ length: rowCount }, (_, i) => {
    return columns.reduce((acc, col) => {
      if (col === 'Name' || col.includes('Name')) {
        acc[col] = `Item ${i + 1}`;
      } else if (col === 'Score' || col.includes('Score')) {
        let score = Math.floor(Math.random() * 100);
        if (filters) {
          score = applyFilters(score, filters);
        }
        acc[col] = score;
      } else if (col === 'Status') {
        const statuses = ['Active', 'Pending', 'Completed'];
        acc[col] = statuses[Math.floor(Math.random() * statuses.length)];
      } else if (col.includes('%')) {
        let value = Math.floor(Math.random() * 100);
        if (filters) {
          value = applyFilters(value, filters);
        }
        acc[col] = `${value}%`;
      } else if (col === 'Class') {
        acc[col] = `Class ${(i % 5) + 1}`;
      } else if (col === 'Engagement') {
        let value = Math.floor(Math.random() * 100);
        if (filters) {
          value = applyFilters(value, filters);
        }
        acc[col] = `${value}%`;
      } else {
        acc[col] = `Value ${i + 1}`;
      }
      return acc;
    }, {});
  });

  // Apply sorting if configured
  if (config?.sortBy && config.sortOrder) {
    const sortCol = config.sortBy;
    rows.sort((a, b) => {
      const aVal = typeof a[sortCol] === 'string' ? parseFloat(a[sortCol]) || 0 : a[sortCol];
      const bVal = typeof b[sortCol] === 'string' ? parseFloat(b[sortCol]) || 0 : b[sortCol];
      return config.sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
    });
  }

  return {
    columns,
    rows,
  };
}

/**
 * Generate student engagement table data by year level with term windows
 * Columns: Year, Window 1, Window 2, Window 3, Window 4, Total
 * Rows: Year levels (Kindergarten, Year 1, Year 2, etc.)
 * Values: Number of engaged students (based on engagement metric config)
 */
function generateStudentEngagementTableData(widget, config) {
  const engagementConfig = config.engagementTable || {};
  const engagementMetric = config.engagementMetric;
  const period = engagementConfig.period || 'year';
  const window = engagementConfig.window || 'term';
  const windowCount = engagementConfig.windowCount || 4;
  
  // Year levels from Kindergarten to Year 12
  const yearLevels = [
    'Kindergarten',
    'Year 1',
    'Year 2',
    'Year 3',
    'Year 4',
    'Year 5',
    'Year 6',
    'Year 7',
    'Year 8',
    'Year 9',
    'Year 10',
    'Year 11',
    'Year 12',
  ];
  
  // Typical student counts per year level (varies by level)
  const getStudentCount = (index) => {
    // Kindergarten and early years have smaller classes
    if (index === 0) return 18 + Math.floor(Math.random() * 4); // 18-21
    if (index <= 2) return 20 + Math.floor(Math.random() * 5); // 20-24
    // Middle years have larger classes
    if (index <= 6) return 24 + Math.floor(Math.random() * 6); // 24-29
    // High school years vary more
    return 22 + Math.floor(Math.random() * 8); // 22-29
  };
  
  // Build columns: Year, Window 1, Window 2, ..., Total
  const columns = ['Year'];
  for (let i = 1; i <= windowCount; i++) {
    columns.push(`Window ${i}`);
  }
  columns.push('Total');
  
  // Generate rows for each year level
  const rows = yearLevels.map((yearLevel, index) => {
    const totalStudents = getStudentCount(index);
    
    // Base engagement rate varies by year level (younger students typically have higher engagement)
    // This is the percentage of students that are engaged
    const baseEngagementRate = 0.70 - (index * 0.015) + (Math.random() * 0.1 - 0.05);
    
    // Generate window values with some variation
    const windowCounts = [];
    let totalEngaged = 0;
    
    for (let i = 0; i < windowCount; i++) {
      // Add variation to base engagement rate for each window
      const variation = (Math.random() * 0.15 - 0.075); // -7.5% to +7.5%
      const engagementRate = Math.max(0.40, Math.min(0.95, baseEngagementRate + variation));
      
      // Calculate number of engaged students (round to nearest integer)
      const engagedCount = Math.round(totalStudents * engagementRate);
      windowCounts.push(engagedCount);
      totalEngaged += engagedCount;
    }
    
    // Calculate average engaged students across all windows (Total)
    const averageEngaged = Math.round(totalEngaged / windowCount);
    
    // Build row object
    const row = {
      Year: yearLevel,
    };
    
    windowCounts.forEach((count, i) => {
      row[`Window ${i + 1}`] = count;
    });
    
    row['Total'] = averageEngaged;
    
    return row;
  });
  
  return {
    columns,
    rows,
  };
}

/**
 * Seeded random number generator for stable random data
 * Same seed always produces the same sequence of random numbers
 */
class SeededRandom {
  constructor(seed) {
    this.seed = seed;
  }

  // Linear congruential generator
  next() {
    this.seed = (this.seed * 9301 + 49297) % 233280;
    return this.seed / 233280;
  }

  // Generate random number between min and max
  random(min = 0, max = 1) {
    return min + this.next() * (max - min);
  }
}

function generateCheckinSummaryTableData(widget, config) {
  // Check if this is the student engagement predesigned card
  if (widget.isPredesigned && widget.predesignedType === 'lsgo-student-engagement-table') {
    return generateStudentEngagementTableData(widget, config);
  }
  
  const { columnConfigs = [], groupBy = ['user'], joins = {}, dateRange } = config;
  
  // Generate 100 users with realistic names
  const firstNames = [
    'Emma', 'James', 'Sophia', 'Michael', 'Olivia', 'William', 'Isabella', 'Alexander', 'Charlotte', 'Daniel',
    'Amelia', 'Matthew', 'Mia', 'David', 'Harper', 'Joseph', 'Evelyn', 'Andrew', 'Abigail', 'Joshua',
    'Emily', 'Christopher', 'Elizabeth', 'Anthony', 'Sofia', 'Mark', 'Avery', 'Thomas', 'Ella', 'Ryan',
    'Madison', 'Nicholas', 'Scarlett', 'Kevin', 'Victoria', 'Brian', 'Aria', 'Jonathan', 'Grace', 'Justin',
    'Chloe', 'Brandon', 'Lily', 'Eric', 'Natalie', 'Jacob', 'Zoe', 'Nathan', 'Hannah', 'Samuel',
    'Addison', 'Benjamin', 'Lillian', 'Jose', 'Aubrey', 'Adam', 'Ellie', 'Patrick', 'Stella', 'Sean',
    'Nora', 'Tyler', 'Maya', 'Aaron', 'Layla', 'Jesse', 'Riley', 'Kyle', 'Aaliyah', 'Noah',
    'Savannah', 'Ethan', 'Leah', 'Connor', 'Audrey', 'Lucas', 'Anna', 'Jack', 'Claire', 'Owen',
    'Lucy', 'Caleb', 'Penelope', 'Hunter', 'Skylar', 'Isaac', 'Violet', 'Evan', 'Bella', 'Gavin',
    'Natalia', 'Mason', 'Aurora', 'Logan', 'Caroline', 'Landon', 'Genesis', 'Carter', 'Ariana', 'Wyatt'
  ];
  
  const lastNames = [
    'Smith', 'Johnson', 'Williams', 'Brown', 'Jones', 'Garcia', 'Miller', 'Davis', 'Rodriguez', 'Martinez',
    'Hernandez', 'Lopez', 'Wilson', 'Anderson', 'Thomas', 'Taylor', 'Moore', 'Jackson', 'Martin', 'Lee',
    'Thompson', 'White', 'Harris', 'Sanchez', 'Clark', 'Ramirez', 'Lewis', 'Robinson', 'Walker', 'Young',
    'Allen', 'King', 'Wright', 'Scott', 'Torres', 'Nguyen', 'Hill', 'Flores', 'Green', 'Adams',
    'Nelson', 'Baker', 'Hall', 'Rivera', 'Campbell', 'Mitchell', 'Carter', 'Roberts', 'Gomez', 'Phillips',
    'Evans', 'Turner', 'Diaz', 'Parker', 'Cruz', 'Edwards', 'Collins', 'Reyes', 'Stewart', 'Morris',
    'Morales', 'Murphy', 'Cook', 'Rogers', 'Gutierrez', 'Ortiz', 'Morgan', 'Cooper', 'Peterson', 'Bailey',
    'Reed', 'Kelly', 'Howard', 'Ramos', 'Kim', 'Cox', 'Ward', 'Richardson', 'Watson', 'Brooks',
    'Chavez', 'Wood', 'James', 'Bennett', 'Gray', 'Mendoza', 'Ruiz', 'Hughes', 'Price', 'Alvarez',
    'Castillo', 'Sanders', 'Patel', 'Myers', 'Long', 'Ross', 'Foster', 'Jimenez', 'Powell', 'Jenkins'
  ];
  
  const mockUsers = Array.from({ length: 100 }, (_, i) => ({
    id: `user${i + 1}`,
    firstName: firstNames[i % firstNames.length],
    lastName: lastNames[Math.floor(i / firstNames.length) % lastNames.length],
  }));
  
  const mockClasses = [
    { id: 1, className: '4A' },
    { id: 2, className: '4B' },
    { id: 3, className: '5A' },
    { id: 4, className: '5B' },
  ];
  
  // Generate rows based on groupBy
  const rows = [];
  
  if (groupBy.includes('user')) {
    // Generate one row per user
    mockUsers.forEach((user, idx) => {
      const row = {};
      
      // Add user information if enabled
      if (joins.includeUsers) {
        row['userName'] = `${user.firstName} ${user.lastName}`;
        row['userId'] = user.id; // Include user ID for profile links
      }
      
      // Add class information if enabled
      if (joins.includeClasses) {
        const classInfo = mockClasses[idx % mockClasses.length];
        row['className'] = classInfo.className;
      }
      
      // Generate emotional state data that creates a distribution across all states
      // Create a distribution where students are distributed across the priority order:
      // 1. Emotional Overload (red, highest to lowest) - ~20 students
      // 2. Heightened State of Emotion (purple) - ~20 students
      // 3. In a state of processing (yellow) - ~20 students
      // 4. Not Ready to Learn (blue) - ~20 students
      // 5. Ready to Learn (green, lowest to highest) - ~20 students
      
      const emotionalStateValues: Record<string, number> = {};
      let dominantState = 'ready-to-learn';
      let dominantValue = 0;
      
      // Use seeded random for stable data generation (based on user index)
      const rng = new SeededRandom(idx + 1000); // Use index + offset as seed
      
      // Generate random emotional state percentages
      // Some students will have 0% for Emotional Overload and/or Heightened State of Emotion
      // 30% chance of 0% Emotional Overload, 30% chance of 0% Heightened State of Emotion
      const hasEmotionalOverload = rng.random() > 0.3; // 70% chance of having EO
      const hasHeightenedEmotion = rng.random() > 0.3; // 70% chance of having HSoE
      
      emotionalStateValues['emotional-overload'] = hasEmotionalOverload ? Math.max(0, rng.random(0, 40)) : 0;
      emotionalStateValues['heightened-emotion'] = hasHeightenedEmotion ? Math.max(0, rng.random(0, 35)) : 0;
      emotionalStateValues['processing'] = Math.max(0, rng.random(0, 40));
      emotionalStateValues['not-ready-to-learn'] = Math.max(0, rng.random(0, 35));
      emotionalStateValues['ready-to-learn'] = Math.max(0, rng.random(0, 50));
      
      // Normalize to sum to 100
      let total = Object.values(emotionalStateValues).reduce((a, b) => a + b, 0);
      Object.keys(emotionalStateValues).forEach((key) => {
        emotionalStateValues[key] = (emotionalStateValues[key] / total) * 100;
      });
      
      // Determine dominant state (highest value)
      let maxValue = 0;
      Object.keys(emotionalStateValues).forEach((key) => {
        if (emotionalStateValues[key] > maxValue) {
          maxValue = emotionalStateValues[key];
          dominantState = key;
          dominantValue = maxValue;
        }
      });
      
      // Final check: ensure all values are positive and sum to exactly 100
      total = Object.values(emotionalStateValues).reduce((a, b) => a + b, 0);
      if (Math.abs(total - 100) > 0.1) {
        // Normalize all values proportionally
        Object.keys(emotionalStateValues).forEach((key) => {
          emotionalStateValues[key] = Math.max(0, (emotionalStateValues[key] / total) * 100);
        });
        // Recalculate dominant value after normalization
        maxValue = 0;
        Object.keys(emotionalStateValues).forEach((key) => {
          if (emotionalStateValues[key] > maxValue) {
            maxValue = emotionalStateValues[key];
            dominantState = key;
            dominantValue = maxValue;
          }
        });
      }
      
      // Store dominant state for row coloring
      row['_dominantState'] = dominantState;
      row['_dominantValue'] = dominantValue;
      
      // Generate column data based on columnConfigs
      columnConfigs.forEach((colConfig) => {
        const columnId = colConfig.id;
        let value = null;
        
        if (colConfig.metricType === 'emotional-state' && colConfig.metricValue) {
          // Use the pre-calculated emotional state value
          value = Math.max(0, Math.min(100, emotionalStateValues[colConfig.metricValue] || 0));
          
          if (colConfig.aggregation === 'count') {
            // Count of check-ins with this state (estimate based on percentage)
            value = Math.floor((value / 100) * 20); // Assume ~20 check-ins total
          } else if (colConfig.aggregation === 'percentage') {
            // Already have the percentage
            value = value;
          } else if (colConfig.aggregation === 'average') {
            value = value;
          } else {
            value = value;
          }
          
          // Format the value
          if (colConfig.format === 'percentage') {
            row[columnId] = value.toFixed(1);
            row[`${columnId}_formatted`] = `${value.toFixed(1)}%`;
          } else {
            row[columnId] = value.toFixed(1);
            row[`${columnId}_formatted`] = value.toFixed(1);
          }
          
          // Handle comparison if enabled
          if (colConfig.comparison?.enabled) {
            const comparisonMultipliers: Record<string, number> = {
              'previous-week': 0.95,
              'previous-month': 0.92,
              'previous-term': 0.88,
              'custom': 0.94,
            };
            const multiplier = comparisonMultipliers[colConfig.comparison.period || 'previous-week'] || 0.95;
            const comparisonValue = value * multiplier;
            const displayAs = colConfig.displayAs || 'both'; // Default to 'both' if not set
            
            if (displayAs === 'value' || displayAs === 'both') {
              row[`${columnId}_comparison`] = comparisonValue.toFixed(1);
              row[`${columnId}_comparison_formatted`] = colConfig.format === 'percentage' 
                ? `${comparisonValue.toFixed(1)}%` 
                : comparisonValue.toFixed(1);
            }
            
            if (displayAs === 'change' || displayAs === 'both') {
              const change = value - comparisonValue;
              row[`${columnId}_change`] = change.toFixed(1);
              row[`${columnId}_change_formatted`] = colConfig.format === 'percentage'
                ? `${change >= 0 ? '+' : ''}${change.toFixed(1)}%`
                : `${change >= 0 ? '+' : ''}${change.toFixed(1)}`;
            }
          }
        } else if (colConfig.metricType === 'emotion-response' && colConfig.metricValue) {
          // Generate mock emotion response data
          const baseValue = 5 + Math.random() * 20; // 5-25% range
          
          if (colConfig.aggregation === 'count') {
            value = Math.floor(5 + Math.random() * 15);
          } else if (colConfig.aggregation === 'percentage') {
            value = baseValue;
          } else {
            value = baseValue;
          }
          
          if (colConfig.format === 'percentage') {
            row[columnId] = value.toFixed(1);
            row[`${columnId}_formatted`] = `${value.toFixed(1)}%`;
          } else {
            row[columnId] = value.toFixed(1);
            row[`${columnId}_formatted`] = value.toFixed(1);
          }
          
          // Handle comparison
          if (colConfig.comparison?.enabled) {
            const comparisonMultipliers: Record<string, number> = {
              'previous-week': 0.95,
              'previous-month': 0.92,
              'previous-term': 0.88,
              'custom': 0.94,
            };
            const multiplier = comparisonMultipliers[colConfig.comparison.period || 'previous-week'] || 0.95;
            const comparisonValue = value * multiplier;
            const displayAs = colConfig.displayAs || 'both'; // Default to 'both' if not set
            
            if (displayAs === 'value' || displayAs === 'both') {
              row[`${columnId}_comparison`] = comparisonValue.toFixed(1);
              row[`${columnId}_comparison_formatted`] = colConfig.format === 'percentage' 
                ? `${comparisonValue.toFixed(1)}%` 
                : comparisonValue.toFixed(1);
            }
            
            if (displayAs === 'change' || displayAs === 'both') {
              const change = value - comparisonValue;
              row[`${columnId}_change`] = change.toFixed(1);
              row[`${columnId}_change_formatted`] = colConfig.format === 'percentage'
                ? `${change >= 0 ? '+' : ''}${change.toFixed(1)}%`
                : `${change >= 0 ? '+' : ''}${change.toFixed(1)}`;
            }
          }
        } else if (colConfig.metricType === 'check-in-rate') {
          // Check-in rate calculation
          const checkInRate = 70 + Math.random() * 20; // 70-90% range
          
          if (colConfig.aggregation === 'percentage') {
            value = checkInRate;
          } else if (colConfig.aggregation === 'count') {
            value = Math.floor(14 + Math.random() * 6); // 14-20 check-ins
          } else {
            value = checkInRate;
          }
          
          if (colConfig.format === 'percentage') {
            row[columnId] = value.toFixed(1);
            row[`${columnId}_formatted`] = `${value.toFixed(1)}%`;
          } else {
            row[columnId] = value.toFixed(1);
            row[`${columnId}_formatted`] = value.toFixed(1);
          }
        } else {
          // Default fallback
          row[columnId] = 'N/A';
          row[`${columnId}_formatted`] = 'N/A';
        }
      });
      
      rows.push(row);
    });
  } else if (groupBy.includes('class')) {
    // Generate one row per class
    mockClasses.forEach((classInfo) => {
      const row = {};
      
      if (joins.includeClasses) {
        row['className'] = classInfo.className;
      }
      
      // Similar column generation logic as above
      columnConfigs.forEach((colConfig) => {
        const columnId = colConfig.id;
        // Generate aggregated data per class (summarize all users in class)
        const baseValue = 30 + Math.random() * 40;
        
        if (colConfig.aggregation === 'count') {
          row[columnId] = Math.floor(20 + Math.random() * 30);
        } else if (colConfig.aggregation === 'percentage') {
          row[columnId] = baseValue.toFixed(1);
          row[`${columnId}_formatted`] = `${baseValue.toFixed(1)}%`;
        } else {
          row[columnId] = baseValue.toFixed(1);
          row[`${columnId}_formatted`] = baseValue.toFixed(1);
        }
      });
      
      rows.push(row);
    });
  }
  
  // Sort rows by emotional state priority:
  // For each category, order from highest % to lowest % until we hit 0%, then move to next category
  // Priority order: 1. Emotional Overload, 2. Heightened State of Emotion, 3. Not Ready to Learn, 4. In a state of processing, 5. Ready to Learn
  const statePriority: Record<string, number> = {
    'emotional-overload': 1,
    'heightened-emotion': 2,
    'not-ready-to-learn': 3,
    'processing': 4,
    'ready-to-learn': 5,
  };
  
  // Get the column IDs for each emotional state
  const getColumnIdForState = (state) => {
    const colConfig = columnConfigs.find((col) => 
      col.metricType === 'emotional-state' && col.metricValue === state
    );
    return colConfig ? colConfig.id : null;
  };
  
  rows.sort((a, b) => {
    // Sort by each state in priority order
    for (const [state] of Object.entries(statePriority).sort(([, p1], [, p2]) => p1 - p2)) {
      const columnId = getColumnIdForState(state);
      if (!columnId) continue;
      
      const aValue = parseFloat(a[columnId] || '0');
      const bValue = parseFloat(b[columnId] || '0');
      
      // If both are 0, move to next category
      if (aValue === 0 && bValue === 0) {
        continue;
      }
      
      // If one is 0 and the other isn't, the non-zero one comes first
      if (aValue === 0) return 1;
      if (bValue === 0) return -1;
      
      // Both are > 0, sort by value descending (highest to lowest)
      return bValue - aValue;
    }
    
    // If all states are equal, maintain original order
    return 0;
  });
  
  // Extract column labels for display
  // Build columns array ensuring comparison columns appear immediately after their original column
  const columns = [];
  
  // Add user/class columns first if enabled (but skip 'col-user' which is the Student column)
  if (joins.includeUsers) {
    columns.push('userName');
  }
  if (joins.includeClasses) {
    columns.push('className');
  }
  
  // Define column order priority (matching row sorting priority)
  const columnOrderPriority: Record<string, number> = {
    'emotional-overload': 1,
    'heightened-emotion': 2,
    'not-ready-to-learn': 3,
    'processing': 4,
    'ready-to-learn': 5,
  };
  
  // Sort column configs by priority order (matching row sorting)
  const sortedColumnConfigs = [...columnConfigs]
    .filter((col) => col.id !== 'col-user') // Remove Student column
    .sort((a, b) => {
      // Get metricValue from each column config
      const aMetricValue = a.metricValue || '';
      const bMetricValue = b.metricValue || '';
      
      const aPriority = columnOrderPriority[aMetricValue] || 999;
      const bPriority = columnOrderPriority[bMetricValue] || 999;
      
      return aPriority - bPriority;
    });
  
  // Add each column config followed by its comparison columns (if enabled)
  sortedColumnConfigs.forEach((col) => {
    // Add the main column
    columns.push(col.id);
    
    // Add comparison columns immediately after the main column
    if (col.comparison?.enabled) {
      const displayAs = col.displayAs || 'both'; // Default to 'both' if not set
      if (displayAs === 'value' || displayAs === 'both') {
        columns.push(`${col.id}_comparison`);
      }
      if (displayAs === 'change' || displayAs === 'both') {
        columns.push(`${col.id}_change`);
      }
    }
  });
  
  // Apply custom sorting if configured (overrides emotional state sorting)
  if (config.sortBy && config.sortOrder) {
    const sortCol = config.sortBy;
    rows.sort((a, b) => {
      const aVal = typeof a[sortCol] === 'string' ? parseFloat(a[sortCol]) || 0 : a[sortCol];
      const bVal = typeof b[sortCol] === 'string' ? parseFloat(b[sortCol]) || 0 : b[sortCol];
      return config.sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
    });
  }
  
  return {
    columns,
    rows,
  };
}

export function generateMockInsightData(widget) {
  const config = widget.config;
  const condition = config?.condition;
  
  // If message is provided and is an array, use it directly
  if (config?.message && Array.isArray(config.message)) {
    return {
      message: config.message,
      type: config?.type || 'info',
    };
  }
  
  if (condition) {
    // Generate insight based on condition
    const metric = condition.metric || 'ready-to-learn';
    const operator = condition.operator || 'less-than';
    const value = condition.value;
    
    // Simulate checking condition (in real app, this would query actual data)
    const simulatedValue = 45; // Example value
    
    let message = '';
    let type = config?.type || 'info';
    
    if (operator === 'less-than' && simulatedValue < (typeof value === 'number' ? value : value[0])) {
      message = config?.message || `${metric} is below threshold (${simulatedValue}%)`;
      type = 'warning';
    } else if (operator === 'greater-than' && simulatedValue > (typeof value === 'number' ? value : value[1])) {
      message = config?.message || `${metric} is above threshold (${simulatedValue}%)`;
      type = 'success';
    } else {
      message = config?.message || `${metric} is within normal range (${simulatedValue}%)`;
      type = 'info';
    }
    
    return {
      message,
      type,
    };
  }
  
  // Fallback to random insights
  const insights = [
    'Performance has improved by 15% this week',
    'Three students need additional support',
    'Engagement levels are above average',
    'Ready to Learn rate is trending upward',
  ];

  return {
    message: insights[Math.floor(Math.random() * insights.length)],
    type: config?.type || 'info',
  };
}

/**
 * Generate mock data for pie/doughnut charts
 * Shows emotional states as percentages that sum to 100%
 */
function generateMockPieChartData(widget, chartType: 'pie' | 'doughnut') {
  const config = widget.config;
  const enabledSources = widget.dataSources.filter((ds) => ds.enabled);
  
  if (enabledSources.length === 0) {
    return {
      labels: [],
      datasets: [],
    };
  }

  // Generate labels and data from enabled sources
  const labels = [];
  const data = [];
  const backgroundColor = [];
  const borderColor = [];

  // Base percentages for emotional states (will be normalized to sum to 100%)
  const basePercentages: Record<string, number> = {
    'ready-to-learn': 35,
    'processing': 25,
    'not-ready': 20,
    'heightened-emotion': 12,
    'emotional-overload': 8,
  };

  let totalPercentage = 0;

  // Ensure we have all 5 emotional states, even if not all are enabled
  const allEmotionalStates = [
    { id: 'ready-to-learn', name: 'Ready to Learn', defaultColor: '#14B8A6' }, // Teal
    { id: 'processing', name: 'In a state of processing', defaultColor: '#EAB308' }, // Mustard
    { id: 'not-ready', name: 'Not Ready to Learn', defaultColor: '#2196F3' },
    { id: 'heightened-emotion', name: 'Heightened State of Emotion', defaultColor: '#9C27B0' },
    { id: 'emotional-overload', name: 'Emotional Overload', defaultColor: '#F44336' },
  ];

  // Process enabled sources first
  enabledSources.forEach((source) => {
    const sourceInfo = getDataSourceById(source.sourceId);
    if (sourceInfo?.metricCategory === 'emotional-state') {
      labels.push(source.label);
      const baseValue = basePercentages[source.sourceId] || 20;
      // Add some variation
      const variation = (Math.random() * 10 - 5); // -5 to +5
      const value = Math.max(5, Math.min(50, baseValue + variation));
      data.push(value);
      totalPercentage += value;
      
      // Use source color or default
      const sourceColor = source.color || sourceInfo?.defaultColor || '#14B8A6'; // Teal (default for Ready to Learn)
      backgroundColor.push(sourceColor);
      borderColor.push(sourceColor);
    }
  });

  // Track which states we've already added
  const addedStateIds = new Set();
  enabledSources.forEach((source) => {
    const sourceInfo = getDataSourceById(source.sourceId);
    if (sourceInfo?.metricCategory === 'emotional-state') {
      addedStateIds.add(source.sourceId);
    }
  });

  // Add any missing emotional states to ensure we have all 5
  allEmotionalStates.forEach((state) => {
    if (!addedStateIds.has(state.id)) {
      // Add missing state with default percentage
      labels.push(state.name);
      const baseValue = basePercentages[state.id] || 20;
      const value = Math.max(5, Math.min(50, baseValue));
      data.push(value);
      totalPercentage += value;
      backgroundColor.push(state.defaultColor);
      borderColor.push(state.defaultColor);
    }
  });

  // Normalize to sum to 100%
  if (totalPercentage > 0 && Math.abs(totalPercentage - 100) > 0.1) {
    const scaleFactor = 100 / totalPercentage;
    for (let i = 0; i < data.length; i++) {
      data[i] = Math.round(data[i] * scaleFactor * 10) / 10;
    }
  }

  return {
    labels,
    datasets: [
      {
        label: 'Emotional States',
        data,
        backgroundColor,
        borderColor,
        borderWidth: 2,
      },
    ],
  };
}

